<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import integrationAPI from 'dashboard/api/integrations';
import Button from 'dashboard/components-next/button/Button.vue';
import PurchaseProjectItemBadge from './PurchaseProjectItemBadge.vue';

const props = defineProps({
  existingProductIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['addProduct']);

const { t, locale } = useI18n();

const isOpen = ref(false);
const isLoading = ref(false);
const hasLoadError = ref(false);
const products = ref([]);
const variantLabel = count =>
  count > 1
    ? t('PURCHASE_PROJECTS.SEARCH.VARIANTS')
    : t('PURCHASE_PROJECTS.SEARCH.VARIANT');
const searchQuery = ref('');
const pendingProduct = ref(null);
const pendingVariantId = ref('');
let requestSequence = 0;

const filteredProducts = computed(() => products.value);

const isAlreadyAdded = productId =>
  props.existingProductIds.map(String).includes(String(productId));

/**
 * Returns the picker stock status from a product-level availability total.
 *
 * @param {number} totalAvailable Product availability across variants.
 * @returns {'available'|'low_stock'|'unavailable'}
 */
const stockStatusFor = totalAvailable => {
  if (totalAvailable === 0) return 'unavailable';
  if (totalAvailable === 1) return 'low_stock';
  return 'available';
};

/**
 * Builds a compact product metadata label.
 *
 * @param {Object} product Picker product.
 * @returns {string}
 */
const productMetaLabel = product =>
  [
    product.sku,
    `${product.variants.length} ${variantLabel(product.variants.length)}`,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Formats product money with the current dashboard locale and the Shopify
 * variant currency when available.
 *
 * @param {number} amount Product amount.
 * @param {string|undefined} currency Shopify variant currency.
 * @returns {string}
 */
const formatMoney = (amount, currency) => {
  const value = Number(amount || 0);
  const options = currency ? { style: 'currency', currency } : {};

  return new Intl.NumberFormat(locale.value, options).format(value);
};

/**
 * Builds the stock suffix for a variant option.
 *
 * @param {Object} variant Picker variant.
 * @returns {string}
 */
const variantStockLabel = variant => {
  if (variant.stock === 0) {
    return t('PURCHASE_PROJECTS.SEARCH.UNAVAILABLE_OPTION');
  }
  if (variant.stock === 1) {
    return t('PURCHASE_PROJECTS.SEARCH.ONE_IN_STOCK');
  }
  return '';
};

/**
 * Converts cached Shopify API products into the purchase project picker shape.
 *
 * @param {Object} product Cached Shopify product payload.
 * @returns {Object} Picker product.
 */
const normalizeProduct = product => {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const normalizedVariants = variants.map(variant => {
    const selectedOptions = Array.isArray(variant.selected_options)
      ? variant.selected_options
      : [];
    const optionLabel = selectedOptions
      .map(option => [option.name, option.value].filter(Boolean).join(': '))
      .filter(Boolean)
      .join(' / ');

    return {
      id: variant.id,
      label: optionLabel || variant.title || variant.sku || product.title,
      stock: Number(variant.available_quantity || 0),
      sku: variant.sku,
      price: Number(variant.price || 0),
      compareAtPrice: variant.compare_at_price
        ? Number(variant.compare_at_price)
        : null,
      currency: variant.currency,
      image: variant.image_url,
    };
  });
  const primaryVariant = normalizedVariants[0] || {};
  const totalAvailable = normalizedVariants.reduce(
    (sum, variant) => sum + Number(variant.stock || 0),
    0
  );

  return {
    id: product.id,
    title: product.title,
    sku: primaryVariant.sku || '',
    price: primaryVariant.price || 0,
    compareAtPrice: primaryVariant.compareAtPrice,
    currency: primaryVariant.currency,
    image: product.featured_image_url || primaryVariant.image || '',
    stockStatus: stockStatusFor(totalAvailable),
    variants: normalizedVariants,
  };
};

/**
 * Loads cached Shopify products for the current account.
 *
 * @returns {Promise<void>}
 */
const fetchProducts = async () => {
  requestSequence += 1;
  const sequence = requestSequence;
  isLoading.value = true;
  hasLoadError.value = false;

  try {
    const { data } = await integrationAPI.searchShopifyProducts({
      query: searchQuery.value.trim(),
      limit: 20,
    });
    if (sequence !== requestSequence) return;

    products.value = Array.isArray(data?.items)
      ? data.items.map(normalizeProduct)
      : [];
  } catch {
    if (sequence !== requestSequence) return;

    products.value = [];
    hasLoadError.value = true;
  } finally {
    if (sequence === requestSequence) {
      isLoading.value = false;
    }
  }
};

watch(searchQuery, () => {
  if (isOpen.value) fetchProducts();
});

const selectProduct = product => {
  if (pendingProduct.value?.id === product.id) {
    pendingProduct.value = null;
    pendingVariantId.value = '';
  } else {
    pendingProduct.value = product;
    pendingVariantId.value = product.variants[0]?.id ?? '';
  }
};

const close = () => {
  isOpen.value = false;
};

const confirmAdd = () => {
  if (!pendingProduct.value) return;
  emit('addProduct', {
    productId: pendingProduct.value.id,
    variantId: pendingVariantId.value,
    quantity: 1,
    sellerNote: '',
    isRecommended: false,
    sortOrder: Date.now(),
  });
  pendingProduct.value = null;
  pendingVariantId.value = '';
  close();
};

const open = () => {
  searchQuery.value = '';
  pendingProduct.value = null;
  pendingVariantId.value = '';
  isOpen.value = true;
  fetchProducts();
};

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[70] flex items-end justify-center pointer-events-none transition-colors duration-200 ease-in-out"
      :class="
        isOpen
          ? 'pointer-events-auto bg-n-alpha-black1 backdrop-blur-[4px]'
          : 'bg-transparent'
      "
      @click.self="close"
    >
      <div
        class="w-full sm:max-w-lg h-[92vh] max-h-[92vh] pointer-events-auto bg-n-solid-2 overflow-y-auto rounded-t-[1.5rem] sm:rounded-t-2xl shadow-xl transition-transform duration-200 ease-in-out"
        :class="isOpen ? 'translate-y-0' : 'translate-y-full'"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 pt-4 pb-3">
          <div class="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-x"
              class="!rounded-full bg-n-alpha-black2 shrink-0"
              @click="close"
            />
            <span class="text-base font-semibold text-n-slate-12 truncate">
              {{ t('PURCHASE_PROJECTS.SEARCH.TITLE') }}
            </span>
          </div>
        </div>

        <!-- Search input -->
        <div class="px-4 py-3">
          <div class="relative">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 i-lucide-search size-4 text-n-slate-10"
            />
            <input
              v-model="searchQuery"
              type="text"
              class="no-margin w-full m-0 rounded-lg border border-n-strong bg-n-solid-1 !pl-9 pr-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:outline-none"
              :placeholder="t('PURCHASE_PROJECTS.SEARCH.PLACEHOLDER')"
              autofocus
            />
          </div>
        </div>

        <!-- Product list -->
        <div
          v-if="isLoading"
          class="px-4 py-8 text-sm text-center text-n-slate-11"
        >
          {{ t('PURCHASE_PROJECTS.SEARCH.LOADING') }}
        </div>
        <p
          v-else-if="hasLoadError"
          class="px-4 py-8 text-sm text-center text-n-ruby-11"
        >
          {{ t('PURCHASE_PROJECTS.SEARCH.ERROR') }}
        </p>
        <div
          v-else-if="filteredProducts.length > 0"
          class="flex flex-col divide-y divide-n-strong"
        >
          <div
            v-for="product in filteredProducts"
            :key="product.id"
            class="flex flex-col"
          >
            <!-- Product row -->
            <button
              type="button"
              data-testid="shopify-product-option"
              class="flex items-center gap-3 px-4 py-3 text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              :class="
                pendingProduct?.id === product.id
                  ? 'bg-n-alpha-1'
                  : 'hover:bg-n-alpha-1'
              "
              :disabled="isAlreadyAdded(product.id)"
              @click="!isAlreadyAdded(product.id) && selectProduct(product)"
            >
              <img
                v-if="product.image"
                :src="product.image"
                :alt="product.title"
                class="size-12 shrink-0 rounded-lg object-cover bg-n-alpha-2"
              />
              <span
                v-else
                class="size-12 shrink-0 rounded-lg bg-n-alpha-2 text-n-slate-10 i-lucide-package"
              />
              <div class="flex flex-col flex-1 min-w-0 gap-0.5">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ product.title }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ productMetaLabel(product) }}
                </span>
                <div class="flex items-center gap-2 mt-0.5">
                  <span class="text-xs font-semibold text-n-slate-12">
                    {{ formatMoney(product.price, product.currency) }}
                  </span>
                  <span
                    v-if="
                      product.compareAtPrice &&
                      product.compareAtPrice > product.price
                    "
                    class="text-xs text-n-slate-9 line-through"
                  >
                    {{ formatMoney(product.compareAtPrice, product.currency) }}
                  </span>
                  <PurchaseProjectItemBadge
                    :stock-status="product.stockStatus"
                  />
                </div>
              </div>
              <span
                v-if="isAlreadyAdded(product.id)"
                class="shrink-0 text-xs text-n-slate-10 italic"
              >
                {{ t('PURCHASE_PROJECTS.SEARCH.ALREADY_ADDED') }}
              </span>
              <span
                v-else-if="pendingProduct?.id === product.id"
                class="shrink-0 i-lucide-chevron-up size-4 text-n-slate-10"
              />
              <span
                v-else
                class="shrink-0 i-lucide-plus size-4 text-n-slate-10"
              />
            </button>

            <!-- Inline variant + add -->
            <div
              v-if="pendingProduct?.id === product.id"
              class="flex items-center gap-2 px-4 pb-3 bg-n-alpha-1"
            >
              <div class="relative flex-1">
                <select
                  v-model="pendingVariantId"
                  class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-solid-2 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                >
                  <option
                    v-for="variant in pendingProduct.variants"
                    :key="variant.id"
                    :value="variant.id"
                  >
                    {{ variant.label }}
                    {{ variantStockLabel(variant) }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                />
              </div>
              <Button
                variant="solid"
                color="blue"
                size="sm"
                data-testid="shopify-product-confirm-add"
                :label="t('PURCHASE_PROJECTS.SEARCH.CONFIRM_ADD')"
                :disabled="!pendingVariantId"
                @click="confirmAdd"
              />
            </div>
          </div>
        </div>

        <p v-else class="px-4 py-8 text-sm text-center text-n-slate-11">
          {{ t('PURCHASE_PROJECTS.SEARCH.EMPTY') }}
        </p>
      </div>
    </div>
  </Teleport>
</template>
